/**
 * Email + password auth against better-auth (`/api/auth/*`), plain fetch like the rest of the
 * auth code. Contract: `contracts/src/auth.ts` (binding). Every function returns a result and never
 * throws, so a form can word each failure (`failureFromResponse`).
 *
 * Fixtures mode (e2e builds) answers from `fixture-auth.ts` (invented example.test users). The
 * mode test is written inline (`import.meta.env.VITE_API_MODE !== "live"`) so a live build drops
 * the import at compile time and emits no fixture chunk.
 */
import { AUTH_LINK_PATHS } from "@divespots/contracts";

export type AuthFailure =
  | "invalid" // unknown email and wrong password look the same, on purpose
  | "email_not_verified"
  | "banned"
  | "rate_limited"
  | "invalid_token" // reset link used, expired or malformed
  | "network"
  | "failed";

export type AuthFailed = { ok: false; reason: AuthFailure; retryAfterSec?: number };
export type AuthResult = { ok: true } | AuthFailed;

const ok: AuthResult = { ok: true };
const fail = (reason: AuthFailure, retryAfterSec?: number): AuthFailed =>
  retryAfterSec === undefined ? { ok: false, reason } : { ok: false, reason, retryAfterSec };

/** Seconds from a `Retry-After` header (delta-seconds form only), else undefined. */
export function parseRetryAfter(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number(value.trim());
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : undefined;
}

/** Maps a better-auth error answer to the failure the UI words. */
export function failureFromResponse(
  status: number,
  body: { code?: unknown; message?: unknown } | null,
  retryAfter?: string | null,
): AuthFailed {
  const text = `${String(body?.code ?? "")} ${String(body?.message ?? "")}`;
  if (status === 429) return fail("rate_limited", parseRetryAfter(retryAfter));
  if (/banned/i.test(text)) return fail("banned");
  if (/email[_ ]not[_ ]verified|not verified/i.test(text)) return fail("email_not_verified");
  if (/invalid[_ ]token|token/i.test(text)) return fail("invalid_token");
  if (status === 401 || /invalid[_ ](email[_ ]or[_ ])?password/i.test(text)) {
    return fail("invalid");
  }
  return fail("failed");
}

async function post(path: string, body: unknown): Promise<AuthResult> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return fail("network");
  }
  if (res.ok) return ok;
  let json: { code?: unknown; message?: unknown } | null = null;
  try {
    json = (await res.json()) as typeof json;
  } catch {
    /* non-JSON error body */
  }
  return failureFromResponse(
    res.status,
    json,
    res.headers.get("retry-after") ?? res.headers.get("x-retry-after"),
  );
}

/** Absolute links the mails point to; the locale prefix also picks the mail language. */
export const linkTargets = (origin: string, locale: string) => ({
  verified: `${origin}/${locale}/${AUTH_LINK_PATHS.verified}`,
  resetPassword: `${origin}/${locale}/${AUTH_LINK_PATHS.resetPassword}`,
});

/** Email + password. An unverified address with the right password gets the mail again (`email_not_verified`). */
export async function signInWithPassword(
  email: string,
  password: string,
  locale: string,
  origin = window.location.origin,
): Promise<AuthResult> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).signIn(email, password);
  return post("/api/auth/sign-in/email", {
    email,
    password,
    callbackURL: linkTargets(origin, locale).verified,
  });
}

/** Always "ok" for a fresh, an existing and a refused address: never reveals which. */
export async function signUp(
  input: { name: string; email: string; password: string },
  locale: string,
  origin = window.location.origin,
): Promise<AuthResult> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).signUp(input);
  return post("/api/auth/sign-up/email", {
    ...input,
    callbackURL: linkTargets(origin, locale).verified,
  });
}

/** Always "ok" (divers get a mail, staff and unknown addresses nothing). */
export async function requestPasswordReset(
  email: string,
  locale: string,
  origin = window.location.origin,
): Promise<AuthResult> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).requestReset(email);
  return post("/api/auth/request-password-reset", {
    email,
    redirectTo: linkTargets(origin, locale).resetPassword,
  });
}

/** Sets the new password; never signs in. `invalid_token` for a used, expired or malformed link. */
export async function resetPassword(token: string, newPassword: string): Promise<AuthResult> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).reset(token, newPassword);
  return post("/api/auth/reset-password", { token, newPassword });
}

/** Account page: the current password is required; the other sessions end. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AuthResult> {
  if (import.meta.env.VITE_API_MODE !== "live")
    return (await import("./fixture-auth.ts")).changePassword(currentPassword, newPassword);
  return post("/api/auth/change-password", {
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  });
}
